import { Injectable } from '@angular/core';
import { Response } from '@models/index.model';

@Injectable()
export class UtilsService {

  // clone and unsynchronize an object
  public cloneObj(src) {
    return (JSON.parse(JSON.stringify(src)));
  }

  // capitalize the first letter of a word
  public titleCaseWord(value: string): string {
    if (!value) {
      return value;
    }
    return value[0].toUpperCase() + value.substring(1).toLowerCase();
  }

  // decode the string
  public decodedData(value: string): string {
    return decodeURIComponent(window.atob(value));
  }

  // encode a string | number | boolean
  public encodedData(value: string | number | boolean): string {
    return window.btoa(encodeURIComponent(value));
  }

  // stringify a JSON object
  public stringifyData(value: any): string {
    return JSON.stringify(value);
  }

  // parse a string JSON to object
  public parseData(value: string): any {
    return JSON.parse(value);
  }

  public parseUserPreferenceConfig(config: string): any {
    return JSON.parse(this.decodedData(config));
  }

  public stringifyUserPreferenceConfig(json): string {
    return this.encodedData(JSON.stringify(json));
  }

  public mapResponseData<T>(res: Response<any>): T {
    if (!res.data) {
      throw new Error('Response data is empty!');
    }
    return res.data;
  }

  public getSessionStorage(key: string): any {
    const item: string = sessionStorage.getItem(key);
    try {
      return JSON.parse(item) ?? null;
    } catch(err) {
      // The item is a string value.
      return item;
    }
  }

  public setSessionStorage(key: string, value: any) {
    if (typeof value !== 'string') {
      value = JSON.stringify(value);
    }
    sessionStorage.setItem(key, value);
  }

  public removeSessionStorageItem(key: string) {
    sessionStorage.removeItem(key);
  }

  public getLocalStorage(key: string): any {
    const item: string = localStorage.getItem(key);
    try {
      return JSON.parse(item) ?? null;
    } catch(err) {
      // The item is a string value.
      return item;
    }
  }

  public setLocalStorage(key: string, value: any) {
    if (typeof value !== 'string') {
      value = JSON.stringify(value);
    }
    localStorage.setItem(key, value);
  }

  public removeLocalStorageItem(key: string) {
    localStorage.removeItem(key);
  }

  public round(value: number, precision?: number): number {
    var multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  }

  public parseText64ToImgURL(value:string, ext:string) {
    return `data:image/${ext.toLowerCase()};base64,${value}`;
  }

  public base64ToFile(name: string, extension: string, base64: string) {
    return new File([this._dataURItoBlob(base64, extension)], name, { type: extension });
  }

  private _dataURItoBlob(dataURI, extension) {
    const byteString = window.atob(dataURI);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([int8Array], { type: extension });
    return blob;
  }

  public getFileExtension(filename) {
    return filename.split(".").pop();
  }

  public getFileNameWithoutExt(filename) {
    const ext = this.getFileExtension(filename);
    return filename.replace(`.${ext}`, '');
  }

  public validateFileSize(fileSize:number, sizeValidate:number) {
    const bytesToMb: number = 0.00000095367432;
    if (fileSize * bytesToMb >= sizeValidate) {
      return false;
    } else {
      return true;
    }
  }
  public validateFileType(fileType:string, typesValidate:string[]) {
    // TYPE VALIDATE
    let isValidFile = false;
    for(var index in typesValidate) {
      if(fileType === typesValidate[index]) {
        isValidFile = true;
        break;
      }
    }
    return isValidFile;
  }

  public isExpired(date: string | number | Date) {
    const today = new Date();
    const parseDate = new Date(date);
    if (today > parseDate) {
      return true;
    }
    return false;
  }

  public replaceSubstring(string: string, newString: string, idxInit: number, idxEnd: number): string {
    return string.substring(0, idxInit) + newString + string.substring(idxEnd);
  }
}
